import { USER_ROLES } from "@/constants";
import { PHISH_SERVICE, SEG_SERVICE } from "@/utils/services";
import { useFlag } from "@/composables/useFlags/useFlag";
import { createFlagRouteGuard } from "@/router/flagRouteGuard";

const { isFeatureAvailable: isQuarantineConfigurationAvailable } = useFlag(
  "QUARANTINE_CONFIGURATION",
);

const { isFeatureAvailable: isGeoblockingAvailable } = useFlag(
  "GEOBLOCKING_CONFIG_PAGE",
);

const { isFeatureAvailable: isPatternFilteringAvailable } =
  useFlag("PATTERN_FILTERING");

const { isFeatureAvailable: isBlockListAvailable } = useFlag(
  "BLOCK_LIST_MAIL_FLOW_LINK",
);

export const mspRoutes = {
  path: "",
  component: () => import("@/layouts/Msp/MspLayout.vue"),
  children: [
    {
      path: "email_security",
      name: "msp-email_security",
      component: () => import("@/modules/PhishTitan/views/Msp/index.vue"),
      meta: {
        solutionId: PHISH_SERVICE,
      },
      redirect: {
        name: "msp-email_security-overview",
      },
      children: [
        {
          path: "overview",
          name: "msp-email_security-overview",
          component: () =>
            import("@/modules/PhishTitan/views/Msp/Overview/index.vue"),
          exact: false,
          meta: {
            authorize: [USER_ROLES.SYSTEM, USER_ROLES.SALES, USER_ROLES.MSP],
          },
        },
        {
          path: "email-history",
          name: "msp-email_security-email-history",
          component: () =>
            import("@/modules/PhishTitan/views/Common/EmailHistory/EmailHistory.vue"),
        },
        {
          path: "quarantine",
          name: "msp-email_security-quarantine",
          meta: {
            solutionId: SEG_SERVICE,
          },
          component: () =>
            import("@/modules/PhishTitan/views/Common/Quarantine/Quarantine.vue"),
        },
        {
          path: "reports",
          name: "msp-email_security-reports",
          exact: true,
          redirect: {
            name: "summaryMspReport",
          },
          children: [
            {
              path: "summary",
              name: "summaryMspReport",
              component: () =>
                import("@/modules/PhishTitan/views/Common/Reports/Summary.vue"),
            },
            {
              path: "insights",
              name: "insightsMspReport",
              component: () =>
                import("@/modules/PhishTitan/views/Common/Reports/Insights.vue"),
            },
          ],
        },
        {
          path: "notifications",
          name: "msp-email_security-notifications",
          component: () =>
            import("@/modules/PhishTitan/views/Common/Notifications/Notifications.vue"),
        },
        {
          path: "configuration",
          name: "msp-email_security-configuration",
          exact: true,
          redirect: { name: "msp-configuration-mail-flow-settings" },
          children: [
            {
              path: "mail_flow",
              name: "msp-configuration-mail-flow",
              component: () =>
                import("@/modules/PhishTitan/views/Msp/Configuration/MailFlow/MailFlow.vue"),
              exact: true,
              redirect: { name: "msp-configuration-mail-flow-settings" },
              children: [
                {
                  path: "settings",
                  name: "msp-configuration-mail-flow-settings",
                  component: () =>
                    import("@/modules/PhishTitan/views/Common/mail-flow-settings/mail-flow-settings.index.vue"),
                },
                {
                  path: ":legacyTab(remediation|exploited_domains|anti_spoofing|graymail)",
                  redirect: { name: "msp-configuration-mail-flow-settings" },
                },
                {
                  path: "frontline",
                  name: "msp-configuration-mail-flow-frontline",
                  meta: {
                    solutionId: SEG_SERVICE,
                  },
                  component: () =>
                    import("@/modules/PhishTitan/views/Common/Frontline/Frontline.vue"),
                },
              ],
            },
            {
              path: "link_lock",
              name: "msp-email_security-config-link-lock",
              exact: true,
              component: () =>
                import("@/modules/PhishTitan/views/Common/LinkLock/LinkLock.vue"),
            },
            {
              path: "exemptions",
              name: "msp-email_security-config-exemptions",
              exact: true,
              component: () =>
                import("@/modules/PhishTitan/views/Common/Exemptions/Exemptions.vue"),
            },
            {
              path: "pattern_filtering",
              name: "msp-email_security-config-pattern-filtering",
              exact: true,
              component: () =>
                import("@/modules/PhishTitan/views/Common/PatternFiltering/PatternFiltering.vue"),
              beforeEnter: createFlagRouteGuard(
                isPatternFilteringAvailable,
                "msp-configuration-mail-flow",
              ),
            },
            {
              path: "email_digest",
              name: "msp-email_security-config-email-digest",
              exact: true,
              component: () =>
                import("@/modules/PhishTitan/views/Common/quarantine-configuration/quarantine-configuration.index.vue"),
              beforeEnter: createFlagRouteGuard(
                isQuarantineConfigurationAvailable,
                "msp-configuration-mail-flow",
              ),
            },
            {
              path: "block_list",
              name: "msp-email_security-config-block-list",
              exact: true,
              component: () =>
                import("@/modules/PhishTitan/views/Common/BlockList/BlockList.vue"),
              beforeEnter: createFlagRouteGuard(
                isBlockListAvailable,
                "msp-configuration-mail-flow",
              ),
            },
            {
              path: "geoblocking",
              name: "msp-email_security-config-geoblocking",
              exact: true,
              component: () =>
                import("@/modules/PhishTitan/views/Common/Geoblocking/Geoblocking.vue"),
              beforeEnter: createFlagRouteGuard(
                isGeoblockingAvailable,
                "msp-configuration-mail-flow",
              ),
            },
          ],
        },
        {
          path: "incidents",
          name: "msp-email_security-incidents",
          component: () =>
            import("@/modules/PhishTitan/views/Msp/Incidents/Overview/index.vue"),
        },
        {
          path: "incidents/incident_summary/:ingressId",
          name: "msp-email_security-incidents-incident-summary",
          component: () =>
            import("@/modules/PhishTitan/views/Msp/Incidents/IncidentSummary/IncidentSummary.vue"),
          redirect: {
            name: "msp-email_security-incidents-incident-summary-details",
          },
          children: [
            {
              path: "details",
              name: "msp-email_security-incidents-incident-summary-details",
              component: () =>
                import("@/modules/PhishTitan/views/Msp/Incidents/IncidentSummary/Details/index.vue"),
            },
            {
              path: "threat-coach",
              name: "msp-email_security-incidents-incident-summary-threat-coach",
              component: () =>
                import("@/modules/PhishTitan/views/Common/Incidents/IncidentSummary/ThreatCoach/ThreatCoach"),
            },
            {
              path: "affected-users",
              name: "msp-email_security-incidents-incident-summary-affected-users",
              component: () =>
                import("@/modules/PhishTitan/views/Msp/Incidents/IncidentSummary/AffectedUsers/index.vue"),
            },
            {
              path: "received-headers",
              name: "msp-email_security-incidents-incident-summary-received-headers",
              component: () =>
                import("@/modules/PhishTitan/views/Msp/Incidents/IncidentSummary/ReceivedHeaders/index.vue"),
            },
            {
              path: "timeline",
              name: "msp-email_security-incidents-incident-summary-received-timeline",
              redirect: {
                name: "msp-email_security-incidents-incident-summary-details",
              },
            },
            {
              path: "timeline",
              name: "msp-email_security-incidents-incident-summary-incident-timeline",
              redirect: {
                name: "msp-email_security-incidents-incident-summary-details",
              },
            },
            {
              path: "audit-logs",
              name: "msp-email_security-incidents-incident-summary-audit-logs",
              redirect: {
                name: "msp-email_security-incidents-incident-summary-details",
              },
            },
            {
              path: "notes",
              name: "msp-email_security-incidents-incident-summary-audit-notes",
              redirect: {
                name: "msp-email_security-incidents-incident-summary-details",
              },
            },
          ],
        },
      ],
    },
  ],
};

export default {};
